/**
 * ============================================================
 * AUTH GATE - sign-in wall + viewer/editor roles
 * ============================================================
 *
 * Wraps the roster so nothing renders until someone is signed in and their
 * role is known. Role resolution now happens server-side (see
 * packages/backend/src/routes/auth.ts): a row in roster_members wins,
 * otherwise any @foundant.com address is an implicit viewer, otherwise
 * there is no access at all.
 *
 * Sign-in is a plain browser redirect to the backend's /api/auth/login,
 * which does the Entra exchange and redirects back here with ?token=<jwt>.
 * There is no MSAL library on the frontend: the whole exchange happens
 * server-side, matching how OPFW's own frontend actually works.
 */

import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const TOKEN_KEY = "auth_token";

/* ============================================================
   Module-level edit flag
   ============================================================
   saveLS() in FreelancerRoster.jsx is a plain function, not a React
   component, so it cannot read context. This mirror variable lets it ask
   "may this user write?" from anywhere.

   It is a convenience, NOT a security control. Anyone can flip it in a
   browser console. The backend's own role checks are the real gate. */

let _canEdit = false;
export function canEditNow() {
  return _canEdit;
}

/* ============================================================
   Context
   ============================================================ */

const AuthContext = createContext({
  session: null,
  email: null,
  role: "none",
  canEdit: false,
  isAdmin: false,
  signOut: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

/* ============================================================
   Main gate
   ============================================================ */

export default function AuthGate({ children }) {
  const [status, setStatus] = useState("loading"); // loading | signedOut | noAccess | ready
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState(null);
  const [role, setRole] = useState("none");

  // On mount: pick up ?token= from the backend's redirect, or fall back to
  // whatever is already stored from an earlier visit.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      localStorage.setItem(TOKEN_KEY, urlToken);
      window.history.replaceState({}, "", window.location.pathname);
      setToken(urlToken);
    } else {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored) {
        setToken(stored);
      } else {
        _canEdit = false;
        setStatus("signedOut");
      }
    }
  }, []);

  // Whenever we have a token, ask the backend who it belongs to.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    fetch(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          localStorage.removeItem(TOKEN_KEY);
          _canEdit = false;
          setStatus("signedOut");
          return;
        }
        const { data } = await res.json();
        _canEdit = data.role === "editor" || data.role === "admin";
        setEmail(data.email);
        setRole(data.role);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_KEY);
        _canEdit = false;
        setStatus("signedOut");
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const signOut = useCallback(() => {
    _canEdit = false;
    localStorage.removeItem(TOKEN_KEY);
    // Full reload clears the in-memory roster cache. Without this, the next
    // person to sign in on this browser would briefly see cached data.
    window.location.href = `${API_BASE}/api/auth/logout`;
  }, []);

  if (status === "loading") return <Shell><div style={S.muted}>Loading...</div></Shell>;
  if (status === "signedOut") return <Shell><SignIn /></Shell>;

  if (role === "none") {
    return (
      <Shell>
        <Message
          title="No access"
          body={`${email} isn't able to view this roster. It's limited to Foundant accounts. If you think that's wrong, contact Logan.`}
          action={<button style={S.linkBtn} onClick={signOut}>Sign out</button>}
        />
      </Shell>
    );
  }

  return (
    <AuthContext.Provider value={{ session: token, email, role, canEdit: _canEdit, isAdmin: role === "admin", signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/* ============================================================
   Sign-in screen
   ============================================================ */

function SignIn() {
  const signIn = () => {
    window.location.href = `${API_BASE}/api/auth/login`;
  };

  return (
    <div style={{ width: 340, textAlign: "left" }}>
      <div style={S.title}>Foundant's Freelancer &amp; Agency Roster</div>
      <div style={{ ...S.muted, marginBottom: 22 }}>
        Sign in with your Foundant Microsoft account to continue.
      </div>
      <button onClick={signIn} style={S.primaryBtn}>Sign in with Microsoft</button>
    </div>
  );
}

/* ============================================================
   Header badge - shows who you are and your access level
   ============================================================ */

export function UserBadge() {
  const { email, role, signOut } = useAuth();
  const [open, setOpen] = useState(false);

  const label = role === "viewer" ? "View only" : role === "editor" ? "Editor" : "Admin";

  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
      {role === "viewer" && (
        <span className="tip" data-tip="You can browse and search. Contact Logan for edit access." style={S.pill}>
          {label}
        </span>
      )}
      <button onClick={() => setOpen((o) => !o)} title={email || ""} style={S.avatar}>
        {(email || "?").charAt(0).toUpperCase()}
      </button>

      {open && (
        <>
          <div style={S.scrim} onClick={() => setOpen(false)} />
          <div style={S.menu}>
            <div style={{ fontSize: 12, color: "#53565A", wordBreak: "break-all", marginBottom: 2 }}>{email}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>{label}</div>
            <button style={S.menuBtn} onClick={signOut}>Sign out</button>
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   Presentation bits
   ============================================================ */

function Shell({ children }) {
  return (
    <div style={S.shell}>
      <link href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,400&display=swap" rel="stylesheet" />
      <div style={S.card}>{children}</div>
    </div>
  );
}

function Message({ title, body, action }) {
  return (
    <div style={{ width: 340, textAlign: "left" }}>
      <div style={S.title}>{title}</div>
      <div style={{ ...S.muted, marginBottom: action ? 18 : 0 }}>{body}</div>
      {action}
    </div>
  );
}

/* Palette matches FreelancerRoster.jsx: navy #002631, teal #007377,
   background #f6f9f8, rule #e8efee. */
const S = {
  shell: { fontFamily: "'Lato', sans-serif", background: "#f6f9f8", color: "#002631", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card: { background: "#fff", border: "1px solid #e8efee", borderRadius: 8, padding: "34px 32px", boxShadow: "0 1px 3px rgba(0,38,49,0.06)" },
  title: { fontWeight: 900, fontSize: 20, letterSpacing: "-0.02em", marginBottom: 8, lineHeight: 1.25 },
  muted: { fontSize: 13, color: "#53565A", lineHeight: 1.55 },
  primaryBtn: { width: "100%", padding: "11px 18px", fontSize: 13, fontWeight: 700, fontFamily: "inherit", background: "#007377", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" },
  linkBtn: { padding: 0, fontSize: 13, fontWeight: 600, fontFamily: "inherit", background: "none", color: "#007377", border: "none", cursor: "pointer", textDecoration: "underline" },
  pill: { fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: "#53565A", background: "#f1f5f4", border: "1px solid #e8efee", borderRadius: 4, padding: "4px 8px", cursor: "default" },
  avatar: { width: 30, height: 30, borderRadius: "50%", border: "1px solid #e8efee", background: "#002631", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
  scrim: { position: "fixed", inset: 0, zIndex: 60 },
  menu: { position: "absolute", top: 38, right: 0, zIndex: 61, background: "#fff", border: "1px solid #e8efee", borderRadius: 6, padding: 12, minWidth: 190, boxShadow: "0 8px 24px rgba(0,38,49,0.12)" },
  menuBtn: { width: "100%", padding: "7px 10px", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", background: "#fff", color: "#002631", border: "1px solid #e8efee", borderRadius: 5, cursor: "pointer", textAlign: "left" },
};
